import { useEffect, useState } from "react";
import { getUnidades } from "@/services/unidade";
import { getTurma } from "@/services/turma";
import { Table, ConfigProvider, theme, Select, Button } from "antd";
import type { ColumnsType } from "antd/es/table";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "@/styles/globals.css";

interface Opcao {
  id: number;
  nome: string;
}

/**
 * Coluna de uma Lista de Plataforma. O `titulo` é usado tanto no cabeçalho da
 * Table quanto no cabeçalho da planilha — os dois nunca saem de sincronia.
 */
export interface ColunaLista<T> {
  titulo: string;
  campo: keyof T & string;
  width?: number;
  /** Formatação aplicada na tela e na exportação (ex.: máscara de CPF). */
  formatar?: (valor: any, registro: T) => string;
}

interface ListaPlataformaProps<T> {
  titulo: string;
  /** Nome do arquivo .xlsx gerado, sem extensão. */
  nomeArquivo: string;
  nomePlanilha: string;
  buscar: (params: { unidadeIds?: string; turmaIds?: string }) => Promise<any>;
  colunas: ColunaLista<T>[];
}

// Valor sentinela da opção "Todas" nos filtros multi-seleção
const TODOS = "";

/**
 * Tela base das Listas de Plataformas (Relatórios > Listas de Plataformas).
 * Filtros de Unidade e Turma no mesmo padrão do MasterSheet; exportação apenas
 * para Excel — estas listas alimentam importadores externos, não têm versão PDF.
 */
export default function ListaPlataforma<T extends object>({
  titulo,
  nomeArquivo,
  nomePlanilha,
  buscar,
  colunas,
}: ListaPlataformaProps<T>) {
  const [unidades, setUnidades] = useState<Opcao[]>([]);
  const [turmas, setTurmas] = useState<Opcao[]>([]);
  const [filtroUnidades, setFiltroUnidades] = useState<(number | string)[]>([TODOS]);
  const [filtroTurmas, setFiltroTurmas] = useState<(number | string)[]>([TODOS]);
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState<T[]>([]);

  useEffect(() => {
    getUnidades().then((res) => setUnidades(res.data || []));
    getTurma().then((res) => setTurmas(res.data || []));
  }, []);

  // Mantém a opção "Todas" exclusiva: selecionar "Todas" limpa as demais e vice-versa
  const handleMultiSelect = (values: (number | string)[], setter: (v: (number | string)[]) => void) => {
    if (values.length === 0) {
      setter([TODOS]);
      return;
    }
    const ultimo = values[values.length - 1];
    if (ultimo === TODOS) {
      setter([TODOS]);
      return;
    }
    setter(values.filter((v) => v !== TODOS));
  };

  const handleBuscar = async () => {
    setLoading(true);
    try {
      const params: { unidadeIds?: string; turmaIds?: string } = {};
      if (!filtroUnidades.includes(TODOS) && filtroUnidades.length > 0) {
        params.unidadeIds = filtroUnidades.join(",");
      }
      if (!filtroTurmas.includes(TODOS) && filtroTurmas.length > 0) {
        params.turmaIds = filtroTurmas.join(",");
      }
      const res = await buscar(params);
      setDados(res?.data || []);
    } finally {
      setLoading(false);
    }
  };

  const formatarValor = (coluna: ColunaLista<T>, registro: T) => {
    const valor = registro[coluna.campo];
    if (coluna.formatar) return coluna.formatar(valor, registro);
    return valor == null ? "" : String(valor);
  };

  const exportToExcel = () => {
    const exportData = dados.map((registro) => {
      const linha: Record<string, string> = {};
      colunas.forEach((coluna) => {
        linha[coluna.titulo] = formatarValor(coluna, registro);
      });
      return linha;
    });

    // Só o cabeçalho das colunas e os dados: estas listas são arquivos de
    // importação das plataformas, qualquer linha extra atrapalha o importador.
    const ws = XLSX.utils.json_to_sheet(exportData);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, nomePlanilha);
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), `${nomeArquivo}.xlsx`);
  };

  const columns: ColumnsType<T> = colunas.map((coluna) => ({
    title: coluna.titulo,
    dataIndex: coluna.campo,
    key: coluna.campo,
    width: coluna.width,
    render: (_: any, registro: T) => formatarValor(coluna, registro),
  }));

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">{titulo}</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-4 items-end mb-8">
          <Select
            mode="multiple"
            value={filtroUnidades}
            onChange={(values) => handleMultiSelect(values, setFiltroUnidades)}
            style={{ minWidth: 260 }}
            placeholder="Unidade"
            maxTagCount="responsive"
            options={[
              { value: TODOS, label: "Todas as Unidades" },
              ...unidades.map((u) => ({ value: u.id, label: u.nome })),
            ]}
          />
          <Select
            mode="multiple"
            value={filtroTurmas}
            onChange={(values) => handleMultiSelect(values, setFiltroTurmas)}
            style={{ minWidth: 260 }}
            placeholder="Turma"
            maxTagCount="responsive"
            options={[
              { value: TODOS, label: "Todas as Turmas" },
              ...turmas.map((t) => ({ value: t.id, label: t.nome })),
            ]}
          />
          <Button type="primary" loading={loading} onClick={handleBuscar}>
            Buscar
          </Button>
          <Button onClick={exportToExcel} disabled={dados.length === 0}>
            Exportar Excel
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={dados}
          rowKey={(_, idx) => String(idx)}
          pagination={{ pageSize: 20 }}
          scroll={{ x: "max-content" }}
          locale={{ emptyText: "Nenhum dado encontrado." }}
        />
      </ConfigProvider>
    </>
  );
}
